import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  FloatButton,
  Image,
  Input,
  Modal,
  Popover,
  Select,
  Table,
  Tooltip,
  message,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { getStatusColor } from "../../../util/statusColor";
import {
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  SyncOutlined,
  UndoOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { AuthContextValue, useAuth } from "../../../providers/AuthProvider";
import {
  useUserUpdateStatus,
  useMyUsersList,
  useUsersList,
  useGetUserStatusCount,
  useResetPassword,
  useLockUnlockUser,
} from "../../../api/users";
import { useGetStatusCount } from "../../../hooks/useGetStatusCount";
import TableFilter from "../../TableFilter";
import TableAction from "../../TableAction";
import CreateUserModal from "../../modals/CreateUserModal";
import { useAssignUserToWarehouse, useCountryStruct } from "../../../api/admin";
import { Region, Zone } from "../../modals/CreateWarehouseModal";

const UsersTable = () => {
  const location = useLocation().pathname.substring(1);
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");

  const handleCreate = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const { user } = useAuth() as AuthContextValue;

  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState("");
  const [viewAll, setViewAll] = useState(false);
  const { mutateAsync: updateStatus, isPending } = useUserUpdateStatus();
  const {
    refetch: lockUnlockUser,
    isLoading: lockChanging,
    isRefetching: lockIsRefetching,
  } = useLockUnlockUser(selectedId);
  const {
    refetch: resetPassword,
    isLoading: resetLoading,
    isRefetching: resetPasswordRefetching,
  } = useResetPassword(selectedId);
  const { data: statuses } = useGetUserStatusCount();
  const filters = useGetStatusCount(statuses?.data.data);
  const [selectedStatus, setSelectedStatus] = useState<string>();
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const {
    data: myUsers,
    isLoading: myUsersIsLoading,
    refetch: refetchMyUsers,
  } = useMyUsersList(page, size);
  const {
    data: allUsers,
    isLoading: allUsersIsLoading,
    refetch: refetchAll,
  } = useUsersList(page, size);
  const [users, setUsers] = useState<any[]>([]);

  const [open, setOpen] = useState(false);
  const [openCreate, setOpenOpenCreate] = useState(false);

  const usersColumn = [
    {
      dataIndex: "fullName",
      title: "Full name",
      render: (text: string, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            {record.avatar_url && (
              <Image
                src={record.avatar_url}
                style={{ width: 50, aspectRatio: 1, borderRadius: 50 }}
              />
            )}
          </div>
          <div>{record.fullName}</div>
        </div>
      ),
    },
    {
      dataIndex: "email",
      title: "Contact Address",
      render: (email: string, record: any) => {
        return (
          <div className="flex flex-col">
            <div>{email}</div>
            <div>{record.phoneNo}</div>
          </div>
        );
      },
    },
    // {
    //   dataIndex: 'phoneNo',
    //   title: 'Phone number'
    // },
    {
      dataIndex: "role",
      title: "User Name/Role",
      render: (role: string, record: any) => {
        return (
          <div className="flex flex-col">
            <div>@{record.username}</div>
            <div>{role}</div>
          </div>
        );
      },
    },
    {
      dataIndex: "isActive",
      title: "Status",
      render: (status: string, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: 100,
              paddingTop: 6,
              paddingBottom: 6,
              borderRadius: 8.5,
              color: getStatusColor(status === "1" ? "ACTIVE" : "INACTIVE"),
              backgroundColor: `${getStatusColor(
                status === "1" ? "ACTIVE" : "INACTIVE"
              )}44`,
            }}
          >
            {status === "1" ? "ACTIVE" : "INACTIVE"}
          </div>
        );
      },
    },
    {
      title: "Actions",
      render: (status: number, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12 }}>
          <Tooltip title="Update Status" overlayStyle={{ pointerEvents: "auto" }}>
            <Button
              type="primary"
              disabled={record.id === user?.userId}
              icon={<SyncOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(record.id);
                setStatus(status === 1 ? "ACTIVE" : "INACTIVE");
                setOpen(true);
              }}
            />
          </Tooltip>
    
          <Tooltip title="Reset Password" overlayStyle={{ pointerEvents: "auto" }}>
            <Button
              loading={resetPasswordRefetching && selectedId === record.id}
              type="primary"
              icon={<UndoOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(record.id);
                resetUserPassword();
              }}
              style={{ backgroundColor: "#f5222d" }}
            />
          </Tooltip>
    
          <Tooltip title="Lock/Unlock User" overlayStyle={{ pointerEvents: "auto" }}>
            <Button
              loading={lockIsRefetching && selectedId === record.id}
              type="primary"
              icon={record.isLocked === 0 ? <UnlockOutlined /> : <LockOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(record.id);
                _lockUnlockUser();
              }}
              style={{
                backgroundColor: record.isLocked === 0 ? "#f55157" : "#87faa6",
              }}
            />
          </Tooltip>
        </div>
      ),
    }
    
  ];

  const onUpdate = async () => {
    if (status) {
      try {
        const result = await updateStatus({
          id: Number(selectedId),
          status: status === "ACTIVE" ? 1 : 0,
        });

        if (result.status === 200) {
          message.success("User status updated successfully.");
        } else {
          message.error("Operation failed");
        }
      } catch (err: any) {
        message.error("Operation failed.");
      } finally {
        setStatus(undefined);
        setOpen(false);
      }
    }
  };

  const filterUsersByStatus = (data: any) => {
    if (selectedFilterIndex === 0) {
      return data;
    } else if (selectedFilterIndex === 1) {
      return data.filter((item: any) => item.isActive === "1");
    } else if (selectedFilterIndex === 2) {
      return data.filter((item: any) => item.isActive === "0");
    }
  };

  useEffect(() => {
    if (user) {
      if (
        user.roleName === "ROLE_KFT_SUPER_ADMIN" &&
        location.indexOf("all-users") >= 0 &&
        !!allUsers &&
        !!allUsers.data &&
        !!allUsers.data.data
      ) {
        if (searchText) {
          const _data = allUsers.data.data.filter(
            (item: any) =>
              item.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.email?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.phoneNo?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.role?.toLowerCase().includes(searchText.toLowerCase())
          );

          const data = filterUsersByStatus(_data);
          setTableData(data);
        } else {
          const data = filterUsersByStatus(allUsers.data.data);
          setTableData(data);
        }
      } else if (
        myUsers &&
        myUsers.data &&
        !!myUsers &&
        !!myUsers.data &&
        !!myUsers.data.data
      ) {
        if (searchText) {
          const _data = myUsers.data.data.filter(
            (item: any) =>
              item.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.email?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.phoneNo?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.username?.toLowerCase().includes(searchText.toLowerCase()) ||
              item.role?.toLowerCase().includes(searchText.toLowerCase())
          );

          const data = filterUsersByStatus(_data);
          setTableData(data);
        } else {
          const data = filterUsersByStatus(myUsers?.data.data);
          setTableData(data);
        }
      }
    }
  }, [user, myUsers, allUsers, location, searchText, selectedFilterIndex]);

  useEffect(() => {
    if (user) {
      if (
        user.roleName === "ROLE_KFT_SUPER_ADMIN" &&
        location.indexOf("all-users") >= 0
      )
        refetchAll();
    } else {
      refetchMyUsers();
    }
  }, [page, size]);

  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE" | undefined>();

  const resetUserPassword = async () => {
    if (selectedId) {
      const result = await resetPassword();
      refetchMyUsers();
      refetchAll();

      setSelectedId("");

      if (
        result &&
        result.data &&
        result.data.data &&
        result.data.data.status === "success"
      ) {
        message.success("Password reseted successfully.");
      } else {
        message.error("Operation failed.");
      }
    }
  };

  const _lockUnlockUser = async () => {
    if (selectedId) {
      const result = await lockUnlockUser();
      refetchMyUsers();
      refetchAll();

      setSelectedId("");

      if (
        result &&
        result.data &&
        result.data.data &&
        result.data.data.status === "success"
      ) {
        const msg =
          result.data.data.data.isLocked === 1
            ? "User locked successfully."
            : "User unlocked successfully.";
        message.success(msg);
      } else {
        message.error("Operation failed.");
      }
    }
  };

  const onCreate = () => {
    setOpenOpenCreate(true);
  };

  return (
    <>
      <CreateUserModal
        open={openCreate}
        hideModal={() => setOpenOpenCreate(false)}
      />
      <Modal
        destroyOnClose={true}
        title={<div style={{ color: "black" }}>Confirmation</div>}
        open={open}
        centered
        onOk={onUpdate}
        okButtonProps={{
          disabled: isPending,
          loading: isPending,
        }}
        onCancel={() => {
          setStatus(undefined);
          setOpen(false);
        }}
        closable
        style={{ color: "black" }}
      >
        <Select
          value={status}
          placeholder="Update status"
          style={{ width: "100%" }}
          onChange={(val: any) => setStatus(val)}
          options={["ACTIVE", "INACTIVE"].map((_status: string) => {
            return { label: _status, value: _status };
          })}
        />
      </Modal>

      <div className="hidden md:block">
        <TableFilter
          filters={filters}
          setSelectedFilterIndex={setSelectedFilterIndex}
        />
      </div>
      <div
        className="hidden md:flex"
        style={{
          alignItems: "center",
          height: 80,
          backgroundColor: "#EEF3F8",
          borderRadius: 8.4,
          marginBottom: 12,
        }}
      >
        <TableAction
          setSearchText={setSearchText}
          onCreateNew={
            location.indexOf("all-users") >= 0 ? undefined : onCreate
          }
        />
      </div>
      <Table
        className="md:block"
        style={{
          cursor: "pointer",
          marginTop: 25,
          position: "relative",
          zIndex: 100,
          overflowX: "scroll",
          // overflowY: "scroll",
          // maxWidth: "100%",
        }}
        onRow={(record, index) => {
          return {
            onClick: (event) => {
              navigate(`/users/${record.id}`);
            },
          };
        }}
        dataSource={tableData}
        columns={usersColumn}
        loading={allUsersIsLoading || myUsersIsLoading}
        onChange={({ current, pageSize }) => {
          setPage(current || 1);
          setSize(pageSize || 10);
        }}
        // scroll={{ x: "max-content" }}
      />

      <div className="md:hidden w-full flex flex-col justify-between ">
        <div className="text-white sticky top-0 pt-4">
          <Input
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            style={{ backgroundColor: "#08453E", color: "white", height: 40 }}
          />
        </div>
        <div className="w-full flex justify-between items-center mt-4 mb-2">
          <div className="flex text-white text-xl">
            {`${location.indexOf("all-users") >= 0 ? "All Users" : "My Users"}`}{" "}
            ({tableData.length || 0})
          </div>
          {tableData.length > 5 && (
            <div className="">
              <Checkbox onChange={(val: any) => setViewAll(val.target.checked)}>
                View All
              </Checkbox>
            </div>
          )}
        </div>
        {tableData &&
          tableData
            .slice(0, viewAll ? tableData.length : 5)
            .map((item: any) => {
              return (
                <Collapse
                  key={item.id}
                  accordion
                  className="mb-1"
                  items={[
                    {
                      key: item.id,
                      label: item.fullName,
                      children: (
                        <div
                          className="w-full flex justify-between items-center"
                          onClick={() => navigate(`/users/${item.id}`)}
                        >
                          <div className="flex flex-col gap-1">
                            <div>Role - {item.role}</div>
                            <div>Email - {item.email}</div>
                            <div>Phone - {item.phoneNo}</div>
                            <div
                              className={`flex justify-center items-center w-[100px] py-[6px] rounded-xl`}
                              style={{
                                color: getStatusColor(
                                  item.isActive === "1" ? "ACTIVE" : "INACTIVE"
                                ),
                                backgroundColor: `${getStatusColor(
                                  item.isActive === "1" ? "ACTIVE" : "INACTIVE"
                                )}44`,
                              }}
                            >
                              {item.isActive === "1" ? "ACTIVE" : "INACTIVE"}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <div>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedId(item.id);
                                  setStatus(
                                    item.isActive === "1"
                                      ? "ACTIVE"
                                      : "INACTIVE"
                                  );
                                  setOpen(true);
                                }}
                                type="primary"
                                className="w-[130px]"
                              >
                                Update Status
                              </Button>
                            </div>
                            <div>
                              <Button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setSelectedId(item.id);

                                  resetUserPassword();
                                }}
                                className="w-[130px]"
                              >
                                Reset Password
                              </Button>
                            </div>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              );
            })}
        {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
          <FloatButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          />
        )}
      </div>
    </>
  );
};

export default UsersTable;
